/**
 * Signing Management Page
 * 
 * CRM view for managing all signing requests.
 * Features:
 * - Table with all signing requests
 * - Status filter
 * - Detail view with audit trail
 * - Actions: Download PDF, Resend Link, Regenerate Token
 */

import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  FileSignature,
  Search,
  RefreshCw,
  Download,
  Send,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Mail,
  Shield,
  Pen,
  Upload,
  FileText,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Status configurations
const STATUS_CONFIG = {
  pending: { label: "Ausstehend", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  sent: { label: "Gesendet", color: "bg-blue-100 text-blue-800", icon: Send },
  otp_sent: { label: "OTP Gesendet", color: "bg-blue-100 text-blue-800", icon: Mail },
  opened: { label: "Geöffnet", color: "bg-purple-100 text-purple-800", icon: Eye },
  otp_verified: { label: "OTP Verifiziert", color: "bg-indigo-100 text-indigo-800", icon: Shield },
  completed_digital: { label: "Digital unterschrieben", color: "bg-green-100 text-green-800", icon: CheckCircle },
  completed_manual: { label: "Manuell unterschrieben", color: "bg-green-100 text-green-800", icon: Upload },
  expired: { label: "Abgelaufen", color: "bg-slate-100 text-slate-800", icon: Clock },
  revoked: { label: "Widerrufen", color: "bg-red-100 text-red-800", icon: XCircle },
};

// Event type configurations
const EVENT_CONFIG = {
  created: { label: "Erstellt", icon: FileSignature },
  sent: { label: "Link gesendet", icon: Send },
  opened: { label: "Seite geöffnet", icon: Eye },
  otp_sent: { label: "OTP gesendet", icon: Mail },
  otp_email_sent: { label: "OTP E-Mail gesendet", icon: Mail },
  otp_verified: { label: "OTP verifiziert", icon: Shield },
  signed_digital: { label: "Digital unterschrieben", icon: Pen },
  upload_completed: { label: "Dokument hochgeladen", icon: Upload },
  pdf_generated: { label: "PDF generiert", icon: FileText },
  completed_digital: { label: "Abgeschlossen (Digital)", icon: CheckCircle },
  completed_manual: { label: "Abgeschlossen (Manuell)", icon: CheckCircle },
  confirmation_email_sent: { label: "Bestätigung gesendet", icon: Mail },
  internal_email_sent: { label: "Interne Benachrichtigung", icon: Mail },
  pdf_downloaded: { label: "PDF heruntergeladen", icon: Download },
  revoked: { label: "Widerrufen", icon: XCircle },
  expired: { label: "Abgelaufen", icon: Clock },
};

// Page size options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function SigningManagementPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [signingRequests, setSigningRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state - initialize from URL params
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialPageSize = parseInt(searchParams.get("pageSize") || "25", 10);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS.includes(initialPageSize) ? initialPageSize : 25);
  
  // Initialize status filter from URL params
  const initialStatus = searchParams.get("status") || "all";
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Detail modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Action states
  const [regenerating, setRegenerating] = useState(false);
  const [resending, setResending] = useState(false);
  
  // Resend dialog state
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendName, setResendName] = useState("");
  
  // Sync status filter with URL
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to page 1 when filter changes
    const newParams = new URLSearchParams(searchParams);
    if (newStatus === "all") {
      newParams.delete("status");
    } else {
      newParams.set("status", newStatus);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPage.toString());
    setSearchParams(newParams);
  };
  
  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1); // Reset to page 1 when page size changes
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageSize", size.toString());
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  // Fetch signing requests
  const fetchSigningRequests = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      let url = `${API_URL}/api/signing-requests/?limit=${pageSize}&skip=${skip}`;
      if (statusFilter && statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Fehler beim Laden");
      
      const data = await response.json();
      setSigningRequests(data.signing_requests || []);
      setTotalCount(data.total_count || 0);
    } catch (err) {
      toast.error("Fehler beim Laden der Signaturanfragen");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchSigningRequests();
  }, [fetchSigningRequests]);

  // Fetch events for a signing request
  const fetchEvents = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/signing-requests/${requestId}/events`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Fehler beim Laden");
      
      const data = await response.json();
      setEvents(data);
    } catch (err) {
      toast.error("Fehler beim Laden der Events");
    }
  };

  // Toggle row expansion
  const toggleRow = async (requestId) => {
    if (expandedRow === requestId) {
      setExpandedRow(null);
      setEvents([]);
    } else {
      setExpandedRow(requestId);
      await fetchEvents(requestId);
    }
  };

  // Open detail modal
  const openDetail = async (request) => {
    setSelectedRequest(request);
    setDetailLoading(true);
    await fetchEvents(request.id);
    setDetailLoading(false);
  };

  // Regenerate token
  const handleRegenerateToken = async (requestId) => {
    setRegenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/signing-requests/${requestId}/regenerate-token`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler");
      }
      
      const data = await response.json();
      
      // Copy new URL to clipboard (with fallback)
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(data.signing_url);
          toast.success("Neuer Link wurde generiert und kopiert!");
        } else {
          // Fallback for non-secure contexts
          toast.success(
            <div>
              <p>Neuer Link wurde generiert!</p>
              <p className="text-xs mt-1 break-all">{data.signing_url}</p>
            </div>
          );
        }
      } catch (clipboardErr) {
        console.warn("Clipboard write failed:", clipboardErr);
        toast.success("Neuer Link wurde generiert!");
      }
      
      // Refresh list
      fetchSigningRequests();
      if (selectedRequest?.id === requestId) {
        await fetchEvents(requestId);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  // Open resend dialog
  const openResendDialog = (request) => {
    setResendTarget(request);
    setResendEmail(request.recipient_email || "");
    setResendName(request.recipient_name || "");
    setResendDialogOpen(true);
  };

  // Resend email with optional updated recipient data
  const handleResendEmail = async (requestId, newEmail = null, newName = null) => {
    setResending(true);
    try {
      const body = {};
      if (newEmail && newEmail !== resendTarget?.recipient_email) {
        body.recipient_email = newEmail;
      }
      if (newName && newName !== resendTarget?.recipient_name) {
        body.recipient_name = newName;
      }
      
      const response = await fetch(`${API_URL}/api/signing-requests/${requestId}/resend`, {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler");
      }
      
      const data = await response.json();
      toast.success(`Signatur-Link wurde an ${data.recipient_email} gesendet!`);
      setResendDialogOpen(false);
      setResendTarget(null);
      fetchSigningRequests();
      if (selectedRequest?.id === requestId) {
        await fetchEvents(requestId);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResending(false);
    }
  };

  // Download signed PDF
  const handleDownloadPDF = async (requestId, type = "signed") => {
    try {
      const response = await fetch(`${API_URL}/api/signing-requests/${requestId}/pdf/${type}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("PDF nicht verfügbar");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Signiert_${requestId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Fehler beim Download");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Gerade eben";
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? "en" : ""}`;
    
    return formatDate(dateString);
  };

  // Filter requests by search (client-side filter for current page)
  const filteredRequests = signingRequests.filter(req => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      req.offer_number?.toLowerCase().includes(search) ||
      req.customer_name?.toLowerCase().includes(search) ||
      req.recipient_name?.toLowerCase().includes(search) ||
      req.recipient_email?.toLowerCase().includes(search)
    );
  });
  
  // Calculate pagination values
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Get status badge
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="signing-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSignature className="h-6 w-6" />
            Signatur-Management
          </h1>
          <p className="text-muted-foreground">
            Übersicht aller Signaturanfragen ({totalCount} Gesamt)
          </p>
        </div>
        
        <Button onClick={fetchSigningRequests} variant="outline" data-testid="refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suche nach Angebot, Kunde, E-Mail..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="status-filter">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Warten auf Unterschrift</SelectItem>
                <SelectItem value="sent">Gesendet</SelectItem>
                <SelectItem value="opened">Geöffnet</SelectItem>
                <SelectItem value="otp_verified">OTP Verifiziert</SelectItem>
                <SelectItem value="completed_digital">Digital unterschrieben</SelectItem>
                <SelectItem value="completed_manual">Manuell unterschrieben</SelectItem>
                <SelectItem value="completed">Abgeschlossen (alle)</SelectItem>
                <SelectItem value="expired">Abgelaufen</SelectItem>
                <SelectItem value="revoked">Widerrufen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <FileSignature className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">Keine Signaturanfragen</h3>
              <p className="text-muted-foreground">
                {statusFilter !== "all" 
                  ? "Keine Anfragen mit diesem Status gefunden."
                  : "Es wurden noch keine Angebote zur Unterschrift versendet."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Angebot</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Gesendet am</TableHead>
                  <TableHead>Letzte Aktivität</TableHead>
                  <TableHead>Verkäufer</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <React.Fragment key={request.id}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      data-testid={`signing-row-${request.id}`}
                    >
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleRow(request.id)}
                        >
                          {expandedRow === request.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">
                        <a 
                          href={`/angebote/${request.offer_id}`}
                          className="text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {request.offer_number || request.offer_id?.slice(0, 8)}
                        </a>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{request.customer_name || request.recipient_name}</div>
                          <div className="text-sm text-muted-foreground">{request.recipient_email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={request.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(request.created_at_utc)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{formatRelativeTime(request.last_activity)}</div>
                          {request.last_activity_type && (
                            <div className="text-xs text-muted-foreground">
                              {EVENT_CONFIG[request.last_activity_type]?.label || request.last_activity_type}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {request.creator_name || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Resend Email Button - only for non-completed */}
                          {!["completed_digital", "completed_manual", "revoked", "expired"].includes(request.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                openResendDialog(request);
                              }}
                              disabled={resending}
                              title="E-Mail erneut senden"
                              data-testid={`resend-btn-${request.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          {/* Regenerate Token Button - only for non-completed */}
                          {!["completed_digital", "completed_manual", "revoked", "expired"].includes(request.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRegenerateToken(request.id);
                              }}
                              disabled={regenerating}
                              title="Neuen Link generieren"
                              data-testid={`regenerate-btn-${request.id}`}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          {/* Download Signed PDF - only for completed */}
                          {(request.status === "completed_digital" || request.status === "completed_manual") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadPDF(request.id, "signed");
                              }}
                              title="Signiertes PDF herunterladen"
                              data-testid={`download-btn-${request.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          {/* View Details Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(request);
                            }}
                            title="Details anzeigen"
                            data-testid={`detail-btn-${request.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Row */}
                    {expandedRow === request.id && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-muted/30 p-4">
                          <div className="space-y-4">
                            {/* Quick Actions */}
                            <div className="flex flex-wrap gap-2">
                              {request.status !== "completed_digital" && request.status !== "completed_manual" && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRegenerateToken(request.id)}
                                    disabled={regenerating}
                                  >
                                    {regenerating ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                    )}
                                    Neuen Link generieren
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openResendDialog(request)}
                                    disabled={resending}
                                  >
                                    {resending ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <Send className="h-4 w-4 mr-2" />
                                    )}
                                    E-Mail erneut senden
                                  </Button>
                                </>
                              )}
                              {(request.status === "completed_digital" || request.status === "completed_manual") && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleDownloadPDF(request.id, "signed")}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Signiertes PDF herunterladen
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadPDF(request.id, "snapshot")}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Original-PDF
                              </Button>
                            </div>
                            
                            {/* Events Timeline */}
                            <div>
                              <h4 className="font-medium mb-2 text-sm">Audit Trail</h4>
                              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                {events.map((event, index) => {
                                  const config = EVENT_CONFIG[event.event_type] || { label: event.event_type, icon: AlertCircle };
                                  const Icon = config.icon;
                                  
                                  return (
                                    <div 
                                      key={event.id || index}
                                      className="flex items-start gap-3 text-sm p-2 rounded hover:bg-muted/50"
                                    >
                                      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium">{config.label}</div>
                                        <div className="text-xs text-muted-foreground">
                                          {formatDate(event.timestamp_utc)}
                                          {event.ip_address && ` • IP: ${event.ip_address}`}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Pagination Controls */}
        {totalCount > 0 && (
          <div className="border-t p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Results Info & Page Size */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground" data-testid="pagination-info">
                  {startItem}-{endItem} von {totalCount} Einträgen
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Pro Seite:</span>
                  <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="w-[80px] h-8" data-testid="page-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(size => (
                        <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Page Navigation */}
              <div className="flex items-center gap-1">
                {/* First Page */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  title="Erste Seite"
                  data-testid="pagination-first"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                
                {/* Previous Page */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  title="Vorherige Seite"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* Page Indicator */}
                <div className="flex items-center gap-1 px-2">
                  <span className="text-sm font-medium" data-testid="pagination-current">
                    Seite {currentPage} von {totalPages || 1}
                  </span>
                </div>
                
                {/* Next Page */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  title="Nächste Seite"
                  data-testid="pagination-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                
                {/* Last Page */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages || loading}
                  title="Letzte Seite"
                  data-testid="pagination-last"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              Signaturanfrage Details
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.offer_number || selectedRequest?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <StatusBadge status={selectedRequest.status} />
                <div className="text-sm text-muted-foreground">
                  Erstellt: {formatDate(selectedRequest.created_at_utc)}
                </div>
              </div>
              
              {/* Info Card */}
              <Card>
                <CardContent className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Empfänger</div>
                    <div className="font-medium">{selectedRequest.recipient_name}</div>
                    <div className="text-sm">{selectedRequest.recipient_email}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Angebot</div>
                    <div className="font-medium">{selectedRequest.offer_number}</div>
                    <div className="text-sm">{selectedRequest.customer_name}</div>
                  </div>
                  {selectedRequest.signed_at_utc && (
                    <div>
                      <div className="text-sm text-muted-foreground">Unterschrieben am</div>
                      <div className="font-medium">{formatDate(selectedRequest.signed_at_utc)}</div>
                    </div>
                  )}
                  {selectedRequest.ip_at_completion && (
                    <div>
                      <div className="text-sm text-muted-foreground">IP bei Unterschrift</div>
                      <div className="font-medium">{selectedRequest.ip_at_completion}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {selectedRequest.status !== "completed_digital" && selectedRequest.status !== "completed_manual" && (
                  <>
                    <Button
                      onClick={() => handleRegenerateToken(selectedRequest.id)}
                      disabled={regenerating}
                    >
                      {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Neuen Link generieren
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => openResendDialog(selectedRequest)}
                      disabled={resending}
                    >
                      {resending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      E-Mail erneut senden
                    </Button>
                  </>
                )}
                {(selectedRequest.status === "completed_digital" || selectedRequest.status === "completed_manual") && (
                  <Button onClick={() => handleDownloadPDF(selectedRequest.id, "signed")}>
                    <Download className="h-4 w-4 mr-2" />
                    Signiertes PDF herunterladen
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedRequest.id, "snapshot")}>
                  <FileText className="h-4 w-4 mr-2" />
                  Original-PDF
                </Button>
              </div>
              
              {/* Audit Trail */}
              <div>
                <h3 className="font-semibold mb-3">Audit Trail</h3>
                {detailLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-2">
                    {events.map((event, index) => {
                      const config = EVENT_CONFIG[event.event_type] || { label: event.event_type, icon: AlertCircle };
                      const Icon = config.icon;
                      
                      return (
                        <div 
                          key={event.id || index}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 border-b last:border-b-0"
                        >
                          <div className="p-2 rounded-full bg-muted">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{config.label}</div>
                            <div className="text-sm text-muted-foreground">
                              {formatDate(event.timestamp_utc)}
                            </div>
                            {event.ip_address && (
                              <div className="text-xs text-muted-foreground mt-1">
                                IP: {event.ip_address}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resend Email Dialog */}
      <Dialog open={resendDialogOpen} onOpenChange={(open) => !open && setResendDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Link erneut senden
            </DialogTitle>
            <DialogDescription>
              E-Mail-Adresse prüfen oder korrigieren und Link erneut senden.
            </DialogDescription>
          </DialogHeader>
          
          {resendTarget && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Empfänger Name</label>
                <Input
                  value={resendName}
                  onChange={(e) => setResendName(e.target.value)}
                  placeholder="Name des Empfängers"
                  data-testid="resend-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">E-Mail-Adresse</label>
                <Input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="E-Mail-Adresse"
                  data-testid="resend-email-input"
                />
                {resendEmail !== resendTarget.recipient_email && resendEmail && (
                  <p className="text-xs text-amber-600">
                    E-Mail wird von "{resendTarget.recipient_email}" auf "{resendEmail}" geändert
                  </p>
                )}
              </div>
              
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <div className="text-muted-foreground mb-1">Angebot</div>
                <div className="font-medium">{resendTarget.offer_number}</div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setResendDialogOpen(false)}
                  disabled={resending}
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={() => handleResendEmail(resendTarget.id, resendEmail, resendName)}
                  disabled={resending || !resendEmail}
                  data-testid="resend-confirm-btn"
                >
                  {resending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Link senden
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
